import { create } from 'zustand'
import type { Database } from '@drive-without-fear/core/db/database.types'
import { supabase } from '@/lib/supabase'

type Profile = Database['public']['Tables']['profiles']['Row']
type ProfileUpdate = Database['public']['Tables']['profiles']['Update']
type AppRole = Profile['role']
type AccountTier = Profile['account_tier']

interface ProfileState {
  profile: Profile | null
  isLoading: boolean
  error: string | null

  // Computed values
  role: AppRole | null
  accountTier: AccountTier | null
  isOnboardingComplete: boolean
  firstName: string | null

  // Actions
  fetchProfile: (userId: string) => Promise<void>
  updateProfile: (data: ProfileUpdate) => Promise<{ error: string | null }>
  clearProfile: () => void
}

export const useProfileStore = create<ProfileState>()((set, get) => ({
  profile: null,
  isLoading: false,
  error: null,

  // Computed values from profile
  get role() {
    return get().profile?.role ?? null
  },
  get accountTier() {
    return get().profile?.account_tier ?? null
  },
  get isOnboardingComplete() {
    return get().profile?.onboarding_completed ?? false
  },
  get firstName() {
    return get().profile?.first_name ?? null
  },

  fetchProfile: async (userId: string) => {
    set({ isLoading: true, error: null })

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error) {
        // Profile might not exist yet for new users (trigger hasn't fired)
        if (error.code === 'PGRST116') {
          // Retry once after a short delay to allow the trigger to complete
          await new Promise((r) => setTimeout(r, 1500))
          const { data: retryData, error: retryError } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', userId)
            .single()

          if (retryData && !retryError) {
            set({ profile: retryData, isLoading: false })
            return
          }
          set({ profile: null, isLoading: false })
          return
        }
        set({ error: error.message, isLoading: false })
        return
      }

      set({ profile: data, isLoading: false })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to fetch profile',
        isLoading: false,
      })
    }
  },

  updateProfile: async (data) => {
    const profile = get().profile
    if (!profile) {
      return { error: 'No profile to update' }
    }

    set({ isLoading: true, error: null })

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from('profiles') as any)
        .update(data)
        .eq('user_id', profile.user_id)

      if (error) {
        set({ error: error.message, isLoading: false })
        return { error: error.message }
      }

      // Update local state
      set({
        profile: { ...profile, ...data },
        isLoading: false,
      })

      return { error: null }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile'
      set({ error: errorMessage, isLoading: false })
      return { error: errorMessage }
    }
  },

  clearProfile: () => {
    set({ profile: null, error: null })
  },
}))

// Selectors for common use cases
export const selectIsAdmin = (state: ProfileState) => state.profile?.role === 'admin'
export const selectIsSupporter = (state: ProfileState) => state.profile?.role === 'supporter'
export const selectIsUser = (state: ProfileState) => state.profile?.role === 'user'
export const selectIsPaidUser = (state: ProfileState) => state.profile?.account_tier === 'paid'
